import { Button, CircularProgress, Tooltip } from '@mui/material';
import { ToolbarButton } from '@mui/x-data-grid-premium';
import { useSelector } from 'react-redux';
import {
  useExportTableMutation,
  type ExportTableRequest,
} from '@/store/exportApi';
import { selectQueryViewingTag } from '@/store/selectQueryViewingTag';
import { useLocalization } from '@/hooks/useLocalization';
import FileDownload from '@mui/icons-material/FileDownload';
import { useGlobalSnackbar } from '@/contexts/snackContext';
import { StyledIconButton } from '../common/StyledIconButton';
import exportUtility from '@/utils/export.utility';

export interface ExportButtonProps {
  columns: ExportTableRequest['columns'];
  label?: string;
  fileName: string;
  disabled?: boolean;
  childProperty?: string;
  dataUrl: string;
  investorId?: ExportTableRequest['investorId'];
  queryParams?: ExportTableRequest['params'];
  isIconOnly?: boolean;
  /** Renders as a data grid toolbar button (with tooltip) to match its siblings. */
  isToolbarButton?: boolean;
}

export function ExportButton({
  columns,
  disabled = false,
  label,
  fileName,
  childProperty,
  dataUrl,
  investorId,
  queryParams,
  isIconOnly,
  isToolbarButton,
}: ExportButtonProps) {
  const { t } = useLocalization();
  const { showErrorSnackbar, showSuccessSnackbar } = useGlobalSnackbar();
  // The gains grid skips its query while the persisted tag is unconfirmed, so
  // that query's isFetching flag stays false and would leave Export enabled.
  const viewingTagPending = !useSelector(selectQueryViewingTag).ready;

  const [exportTable, { isLoading: isSaving }] = useExportTableMutation();

  const handleClick = async () => {
    const filename = exportUtility.generateFileName(fileName);

    try {
      await exportTable({
        dataUrl,
        investorId,
        columns,
        filename,
        childProperty,
        params: queryParams,
      }).unwrap();
      showSuccessSnackbar(t('grid.exportSuccess', { fileName: filename }));
    } catch {
      showErrorSnackbar(t('grid.exportError'));
    }
  };

  if (columns.length === 0) {
    return null;
  }

  if (isToolbarButton) {
    const exportLabel = label ?? t('grid.export');
    return (
      <Tooltip
        title={exportLabel}
        arrow
      >
        {/* span keeps the tooltip working while the button is disabled */}
        <span>
          <ToolbarButton
            onClick={() => void handleClick()}
            disabled={disabled || isSaving || viewingTagPending}
            aria-label={exportLabel}
          >
            {isSaving ? (
              <CircularProgress
                size={16}
                color='inherit'
              />
            ) : (
              <FileDownload fontSize='small' />
            )}
          </ToolbarButton>
        </span>
      </Tooltip>
    );
  }

  if (isIconOnly) {
    return (
      <StyledIconButton
        onClick={() => void handleClick()}
        disabled={disabled || isSaving || viewingTagPending}
        loading={isSaving}
        aria-label={label ?? t('grid.export')}
      >
        <FileDownload />
      </StyledIconButton>
    );
  }

  return (
    <Button
      onClick={() => void handleClick()}
      disabled={disabled || viewingTagPending}
      loadingPosition='start'
      loading={isSaving}
      startIcon={<FileDownload />}
      variant='outlined'
    >
      {label ?? t('grid.export')}
    </Button>
  );
}
